import { useQuery } from '@tanstack/react-query';
import { RoundPhase } from '@uefadigital/panenka';
import { PkAccordionGroup, PkBox, PkButton, PkTab, PkTabPanel, PkTabs } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { Suspense, lazy, memo, useMemo, useState } from 'react';

import { useTicketingOptions } from '../../store';
import { getTicketingData, hydrateMatchWithTickets, groupMatchesByCity, groupMatchesByRound } from '../../utils/data';
import Categories from '../Categories';

import DemandDialog from './DemandDialog';
import InfoVenueTime from './InfoVenueTime';
import Legend from './Legend';

import { getMatchesQuery } from '@queries/matches';
import { withErrorBoundary } from '@shared/ErrorBoundary';
import Loader from '@shared/Loader';
import { useTranslateFn } from '@shared/Translate';
import { Order } from '@shared-types/common.types';
import type { CityMatch, MatchWithTickets, ScheduleMatch, TicketingInfo } from '@shared-types/ticketing.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';

import * as styles from './styles.module.scss';

const ListItemCity = lazy(() => import('./ListItem/Cities'));
const ListItemSchedule = lazy(() => import('./ListItem/Schedule'));

const phase = RoundPhase.TOURNAMENT;

export const TicketPrices = () => {
  const t = useTranslateFn();
  const [isDemandDialogOpen, setDemandDialogOpen] = useState<boolean>(false);
  const {
    options: { competitionId, seasonYear, showDemands, timezone, ticketPricesTitle },
  } = useTicketingOptions();

  const [isDialogOpen, setDialogOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState('schedule');
  const [dialogMatches, setDialogMatches] = useState<MatchWithTickets[]>([]);

  const openDemandDialog = () => {
    setDemandDialogOpen(true);
    GoogleTagManagerHelper.event('Click', "Open what's demand", 'ticketing');
  };
  const closeDemandDialog = () => setDemandDialogOpen(false);

  const handleDialogOpen = (matches: MatchWithTickets[], trackName: string) => {
    setDialogOpen(true);
    setDialogMatches(matches);
    trackName &&
      GoogleTagManagerHelper.event(
        'Click',
        `Open category map - ${trackName}`,
        'ticketing',
        { section_reference: 'ticketing', interaction: `Open category map - ${trackName}` },
        'ticketing',
      );
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogMatches([]);
  };

  const handleTabs = (
    event: CustomEvent<{
      tabId: string;
    }>,
  ) => {
    GoogleTagManagerHelper.event(
      'Click',
      `Tab by ${event.detail.tabId}`,
      'ticketing',
      { section_reference: 'ticketing', interaction: `Tab by ${event.detail.tabId}` },
      'ticketing',
    );
    setSelectedTab(event.detail.tabId);
  };

  const { data: ticketingData } = useQuery<TicketingInfo[]>({
    queryKey: ['ticketing-data', competitionId, seasonYear, phase],
    queryFn: () => getTicketingData(competitionId, seasonYear, phase),
    placeholderData: [],
  });

  const { data: matches } = useQuery(
    getMatchesQuery({
      competitionId,
      seasonYear,
      phase,
      order: Order.ASC,
      offset: 0,
      limit: 100,
    }),
  );

  const ticketMatches = useMemo<MatchWithTickets[]>(
    () => matches.map(hydrateMatchWithTickets(ticketingData)),
    [matches, ticketingData],
  );

  const cities = useMemo(() => groupMatchesByCity(ticketMatches), [ticketMatches]);
  const schedules = useMemo(() => groupMatchesByRound(ticketMatches), [ticketMatches]);

  return (
    <div
      className={clsx(
        styles.ticketsprices,
        'pk-container',
        'pk-py--l pk-py-md--xl2',
        'pk-overflow--hidden',
        'pk-bg--background',
      )}
      pk-theme='light'
    >
      <div className='pk-font--comp-bold pk-col pk-col--span-00-2 pk-col--span-sm-6 pk-col--span-md-10 pk-col--span-lg-10 pk-text--text-01'>
        <h2 className={styles.ticketspricesTitle}>{t(ticketPricesTitle)}</h2>
      </div>
      <div className='pk-col pk-col--span-00-2 pk-text--text-01 pk-text--right'>
        {showDemands && (
          <PkButton className={styles.priceBtn} appearance='text' width='110' onPkClick={openDemandDialog}>
            {t('moreinfo')}
          </PkButton>
        )}
      </div>
      {showDemands && <DemandDialog isOpen={isDemandDialogOpen} onClose={closeDemandDialog} />}
      {isDialogOpen && <Categories data={dialogMatches} isOpen={isDialogOpen} onClose={handleDialogClose} />}
      <PkTabs onPkChange={handleTabs} className='pk-col pk-col--span-12'>
        <PkTab tab-id='schedule' selected={selectedTab === 'schedule'}>
          {t('by_schedule')}
        </PkTab>
        <PkTab tab-id='city' selected={selectedTab === 'city'}>
          {t('by_city')}
        </PkTab>
        <PkTabPanel className={clsx(styles.tabPanel, 'pk-pt--m pk-pt-md--xl')} tab='schedule' slot='content'>
          {!!timezone && <InfoVenueTime />}
          <PkBox rounded='xl' className={styles.tabBox}>
            {selectedTab === 'schedule' && !!schedules.length ? (
              <Suspense fallback={<Loader />}>
                <PkAccordionGroup className={styles.accGroup} multipleOpen>
                  {schedules.map((s: ScheduleMatch, index: number) => (
                    <ListItemSchedule
                      key={`listitem${s.round.id}`}
                      data={s}
                      isLast={index === schedules.length - 1}
                      onDialogOpen={handleDialogOpen}
                    />
                  ))}
                </PkAccordionGroup>
              </Suspense>
            ) : (
              <Loader />
            )}
          </PkBox>
        </PkTabPanel>
        <PkTabPanel tab='city' slot='content' className={clsx(styles.tabPanel, 'pk-pt--m pk-pt-md--xl')}>
          {!!timezone && <InfoVenueTime />}
          <PkBox rounded='xl' className={styles.tabBox}>
            {selectedTab === 'city' && !!cities.length ? (
              <Suspense fallback={<Loader />}>
                <PkAccordionGroup className={styles.accGroup} multipleOpen>
                  {cities.map((c: CityMatch, index: number) => (
                    <ListItemCity
                      key={`listitem-${c.city.id}-${c.stadium.id}`}
                      data={c}
                      isLast={index === cities.length - 1}
                      onDialogOpen={handleDialogOpen}
                    />
                  ))}
                </PkAccordionGroup>
              </Suspense>
            ) : (
              <Loader />
            )}
          </PkBox>
        </PkTabPanel>
      </PkTabs>
      {showDemands && <Legend />}
    </div>
  );
};

export default withErrorBoundary(memo(TicketPrices));
